import React, { useEffect, useState } from "react";
import { api, ApiError, type Property, type Subscription } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { loadRazorpayCheckout } from "../../razorpayCheckout";

const BOOST_PRICE = 49;
const BOOST_DAYS = 7;

function daysLeft(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// Shown on a single independent property's card — lets a Basic-plan owner
// pay once for Featured placement on just this listing instead of
// upgrading the whole account to Pro. Premium/Pro owners already get
// Featured for free (see OWNER_LIMITS.featured), so this hides itself for them.
export function BoostListing({ property, onChanged }: { property: Property; onChanged: () => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [showSheet, setShowSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState<string | null>(null);

  useEffect(() => {
    api.get("/api/subscription").then(setSub).catch(() => {});
    api.get("/api/config").then(({ razorpayKeyId }) => setRazorpayKeyId(razorpayKeyId)).catch(() => {});
  }, []);

  async function waitForBoost() {
    for (let i = 0; i < 6; i++) {
      const fresh: { property: Property } = await api.get(`/api/properties/${property.id}`);
      if (fresh.property.boosted) {
        toast(t("boostListing.boostedToast"));
        onChanged();
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    onChanged();
  }

  async function pay() {
    setBusy(true);
    try {
      if (!razorpayKeyId) throw new ApiError(400, t("boostListing.paymentsNotSetUp"));
      await loadRazorpayCheckout();
      const order = await api.post(`/api/properties/${property.id}/boost/checkout`, {});
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency,
          order_id: order.orderId,
          name: "ToLetBook",
          image: "https://toletbook.in/icons/icon-512.png",
          description: t("boostListing.paymentDesc"),
          handler: () => resolve(),
          modal: { ondismiss: () => reject(new Error("cancelled")) },
        });
        rzp.on("payment.failed", () => reject(new Error("failed")));
        rzp.open();
      });
      setShowSheet(false);
      await waitForBoost();
    } catch (err) {
      if (err instanceof Error && err.message === "failed") toast(t("boostListing.paymentFailedToast"));
      else if (err instanceof ApiError) toast(err.message);
      // "cancelled" fails silently
    } finally {
      setBusy(false);
    }
  }

  // Pro/Premium already get Featured for free — nothing to sell them.
  if (!sub || sub.plan !== "basic") return null;
  // Boosting a property that isn't even listed (or already rented out) does nothing useful.
  if (!property.toLet || property.status === "occupied") return null;

  if (property.boosted && property.featuredUntil) {
    return (
      <div className="boost-active-strip">
        <span className="boost-active-icon">⚡</span>
        <div>
          <div className="boost-active-title">{t("boostListing.activeTitle", { days: daysLeft(property.featuredUntil) })}</div>
          <div className="boost-active-sub">{t("boostListing.activeSub", { date: fmtDate(property.featuredUntil) })}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="boost-cta-row" onClick={(e) => { e.stopPropagation(); setShowSheet(true); }}>
        <span className="boost-cta-icon">⚡</span>
        <div className="boost-cta-text">
          <div className="boost-cta-title">{t("boostListing.ctaTitle")}</div>
          <div className="boost-cta-sub">{t("boostListing.ctaSub", { days: BOOST_DAYS })}</div>
        </div>
        <div className="boost-cta-price">₹{BOOST_PRICE}</div>
      </div>

      {showSheet && (
        <>
          <div className="filter-backdrop" onClick={(e) => { e.stopPropagation(); if (!busy) setShowSheet(false); }} />
          <div className="filter-sheet" style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
            <div className="filter-sheet-handle" />
            <div className="boost-sheet-icon">⚡</div>
            <div className="reg-title" style={{ marginBottom: 2 }}>{t("boostListing.sheetTitle")}</div>
            <div className="sub" style={{ marginBottom: 16 }}>{property.label}</div>
            <div className="card" style={{ textAlign: "left", marginBottom: 16 }}>
              <div className="card-row" style={{ padding: "6px 0" }}>
                <span className="sub">{t("boostListing.placementLabel")}</span>
                <b style={{ fontSize: 13 }}>{t("boostListing.placementValue")}</b>
              </div>
              <div className="card-row" style={{ padding: "6px 0" }}>
                <span className="sub">{t("boostListing.durationLabel")}</span>
                <b style={{ fontSize: 13 }}>{t("boostListing.durationValue", { days: BOOST_DAYS })}</b>
              </div>
              <div className="card-row" style={{ padding: "6px 0" }}>
                <span className="sub">{t("boostListing.scopeLabel")}</span>
                <b style={{ fontSize: 13 }}>{t("boostListing.scopeValue")}</b>
              </div>
            </div>
            <div style={{ fontFamily: "'Baloo 2',sans-serif", fontSize: 30, fontWeight: 700, marginBottom: 2 }}>₹{BOOST_PRICE}</div>
            <div className="field-hint" style={{ marginBottom: 16 }}>{t("boostListing.oneTimeNote")}</div>
            <button className="btn-solid" disabled={busy} onClick={pay} style={{ background: "var(--cash)", color: "var(--cash-ink)" }}>
              {busy ? t("boostListing.processingBtn") : t("boostListing.payBtn", { amount: BOOST_PRICE })}
            </button>
            <button className="link-btn" style={{ marginTop: 10 }} disabled={busy} onClick={() => setShowSheet(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </>
      )}
    </>
  );
}
